import React, { useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useCesium } from '../../../../context/CesiumContext';
import { getReportUsedEntities } from '../../../../lib/accessAnalysisReports';
import { deleteGroundStation, deleteGroundStations } from '../../../../service/groundStationApi';
import { TreeSection, DishIcon, type SortOption } from '../../Common';
import TreeItem, { TreeItemContext } from '../../Satellite/components/TreeItem';
import type { GroundStation } from '../../../../types/mission';
import type { MissionStore } from '../../../../../../../store/MissionSlice';
import { showToastMessage } from '../../../../../../../utils/commonFunctions';
import styles from '../../LeftSideBar.module.scss';
import ConfirmationDialog from '../../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog';

interface GroundStationSectionProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  disabled?: boolean;
}

const toggleSelectedId = (ids: string[], id: string) => (
  ids.includes(id) ? ids.filter(existing => existing !== id) : [...ids, id]
);

function sortItems<T extends { name: string; id: string }>(items: T[], sort: SortOption): T[] {
  return [...items].sort((a, b) => {
    if (sort === 'az') return a.name.localeCompare(b.name);
    if (sort === 'za') return b.name.localeCompare(a.name);
    const aT = parseInt(a.id.replace(/\D/g, '').slice(0, 13), 10) || 0;
    const bT = parseInt(b.id.replace(/\D/g, '').slice(0, 13), 10) || 0;
    return sort === 'newest' ? bT - aT : aT - bT;
  });
}

const GroundStationSection: React.FC<GroundStationSectionProps> = ({ collapsed, onToggleCollapsed, disabled = false }) => {
  const {
    groundStations, selectedItemId, openAddGS, selectTreeItem, toggleGSActive,
    updateGroundStation, setGroundStations, analyserOpen, coverageAnalyserOpen, reportHoverHighlights, shouldBlockUI,
    selectedReportDetail, selectedCoverageReportDetail, satellites, invalidateAccessWindows, addGSModalOpen,
  } = useMDCockpit();
  const userData = useSelector((state: { mission: MissionStore }) => state.mission.userData);
  const cesium = useCesium();
  const { mission_id = '', scenario_id = '' } = useParams<{ mission_id: string; scenario_id: string }>();

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortOption>('newest');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deletingGS, setDeletingGS] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [deletingPending, setDeletingPending] = useState(false);
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);

  React.useEffect(() => {
    if (analyserOpen || coverageAnalyserOpen) {
      setSelectedIds([]);
    }
  }, [analyserOpen, coverageAnalyserOpen]);

  const handleAddGroundStation = useCallback(() => {
    const maxGroundStations = userData?.['feature-allowed']?.['missionModelMaxGroundStation'] || 0;
    const currentGSCount = groundStations.length;

    if (maxGroundStations > 0 && currentGSCount >= maxGroundStations) {
      showToastMessage('error', `You have reached the maximum limit of ${maxGroundStations} ground stations. Please delete an existing ground station to add a new one.`);
      return;
    }

    openAddGS();
  }, [userData, groundStations.length, openAddGS]);

  const hoverGroundStationIds = useMemo(() => new Set(reportHoverHighlights.groundStationIds), [reportHoverHighlights.groundStationIds]);
  const activeReport = selectedReportDetail || selectedCoverageReportDetail;
  const usedEntities = useMemo(() => getReportUsedEntities(activeReport, satellites), [activeReport, satellites]);
  const isReportActive = activeReport !== null;

  const filteredGS = useMemo(() => {
    const q = query.toLowerCase();
    const list = q ? groundStations.filter(g => g.name.toLowerCase().includes(q)) : groundStations;
    return sortItems(list, sort);
  }, [groundStations, query, sort]);

  const deleteGroundStationById = useCallback(async (id: string) => {
    const res: any = await deleteGroundStation(mission_id, scenario_id, id);
    setGroundStations(groundStations.filter(g => g.id !== id));
    invalidateAccessWindows();
    if (selectedItemId === id) selectTreeItem(null, null);
    
    const successMsg = res?.data?.message || res?.message || 'Ground station deleted successfully';
    showToastMessage('success', successMsg);
  }, [mission_id, scenario_id, groundStations, setGroundStations, selectedItemId, selectTreeItem, invalidateAccessWindows]);

  const confirmPendingDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeletingPending(true);
    try {
      await deleteGroundStationById(pendingDelete.id);
    } catch (e: any) {
      console.error('[GroundStation] Delete failed', e);
      const errorMsg = e?.response?.data?.message || e?.response?.data?.Message || e?.data?.message || e?.message || 'Failed to delete ground station';
      showToastMessage('error', errorMsg);
    } finally {
      setPendingDelete(null);
      setDeletingPending(false);
    }
  }, [pendingDelete, deleteGroundStationById]);

  const handleDeleteSelectedGS = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setDeletingGS(true);
    try {
      const res: any = await deleteGroundStations(mission_id, scenario_id, selectedIds);
      setGroundStations(groundStations.filter(gs => !selectedIds.includes(gs.id)));
      invalidateAccessWindows();
      setSelectedIds([]);
      if (selectedItemId && selectedIds.includes(selectedItemId)) selectTreeItem(null, null);
      
      const successMsg = res?.data?.message || res?.message || 'Ground stations deleted successfully';
      showToastMessage('success', successMsg);
    } catch (e: any) {
      console.error('[GroundStation] Bulk delete failed', e);
      const errorMsg = e?.response?.data?.message || e?.response?.data?.Message || e?.data?.message || e?.message || 'Failed to delete selected ground stations';
      showToastMessage('error', errorMsg);
    } finally {
      setConfirmingBulkDelete(false);
      setDeletingGS(false);
    }
  }, [selectedIds, groundStations, setGroundStations, selectedItemId, selectTreeItem, mission_id, scenario_id, invalidateAccessWindows]);

  return (
    <>
      <TreeItemContext.Provider value={{ openMenuId, setOpenMenuId }}>
        <TreeSection
          title="Ground Segments"
          count={groundStations.length}
          onAdd={handleAddGroundStation}
          query={query}
          onQuery={setQuery}
          sort={sort}
          onSort={setSort}
          empty={filteredGS.length === 0}
          emptyText="No stations yet"
          collapsed={collapsed}
          onToggleCollapsed={onToggleCollapsed}
          disabled={disabled || shouldBlockUI}
          modalOpen={addGSModalOpen}
        >
          {filteredGS.map((gs: GroundStation) => (
            <TreeItem
              key={gs.id}
              id={gs.id}
              name={gs.name}
              dot="ok"
              active={gs.active}
              icon={<DishIcon />}
              selected={selectedItemId === gs.id || selectedIds.includes(gs.id) || hoverGroundStationIds.has(gs.id)}
              onSelect={() => {
                selectTreeItem(gs.id, 'groundstation');
              }}
              onToggleActive={() => { toggleGSActive(gs.id); cesium.setGSActive(gs.id, !gs.active); }}
              onEdit={() => { cesium.setClockPlaying(false); openAddGS(gs.id); }}
              onDelete={() => deleteGroundStationById(gs.id)}
              onRequestDelete={() => setPendingDelete({ id: gs.id, name: gs.name })}
              onHover={(id) => { cesium.highlightGroundStation(id); }}
              disabled={shouldBlockUI}
              unusedInReport={isReportActive && !usedEntities.groundStationIds.has(gs.id)}
            />
          ))}
        </TreeSection>
      </TreeItemContext.Provider>

      {ReactDOM.createPortal(
        <>
          <ConfirmationDialog
            type="delete"
            open={pendingDelete !== null}
            title="Delete Ground Station?"
            loading={deletingPending}
            description={
              pendingDelete
                ? `Deleting ${pendingDelete.name} will permanently remove it from the database, and it cannot be restored once deleted.`
                : ''
            }
            confirm_button_text="Delete"
            onConfirm={confirmPendingDelete}
            onCancel={() => setPendingDelete(null)}
          />

          <ConfirmationDialog
            type="delete"
            open={confirmingBulkDelete}
            title="Delete Ground Stations?"
            loading={deletingGS}
            description={`Deleting selected ground stations will permanently remove them from the database, and they cannot be restored once deleted.`}
            confirm_button_text="Delete"
            onConfirm={handleDeleteSelectedGS}
            onCancel={() => setConfirmingBulkDelete(false)}
          />
        </>,
        document.body
      )}
    </>
  );
};

export default GroundStationSection;
